import type {InteractionLimitBanner} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {repoAccessSettingsPath, repoContributorsPath} from '@github-ui/paths'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {CheckIcon, ClockIcon, XIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Button, Flash, Label, Link} from '@primer/react'

import styles from './InterractionLimitsBanner.module.css'

export function InterractionLimitsBanner({interactionLimitBanner}: {interactionLimitBanner: InteractionLimitBanner}) {
  const repo = useCurrentRepository()

  let description

  if (interactionLimitBanner.usersHaveAccess) {
    description = 'Users that have recently created their account will be unable to interact with the repository.'
  } else if (interactionLimitBanner.contributorsHaveAccess) {
    description = (
      <>
        Users that have not previously{' '}
        <Link inline href={repoContributorsPath(repo)}>
          committed
        </Link>{' '}
        to the {repo.defaultBranch} branch of this repository will be unable to interact with the repository.
      </>
    )
  } else {
    description = (
      <>
        Users that are not{' '}
        <Link inline href={repoAccessSettingsPath(repo)}>
          collaborators
        </Link>{' '}
        will not be able to interact with the repository.
      </>
    )
  }

  return (
    <Flash>
      <div>
        <ClockIcon className={styles.Octicon} />
        <span>{interactionLimitBanner.limitTitle}</span>
        <Label variant="success" className={styles.Label}>{`${interactionLimitBanner.currentExpiry} remaining`}</Label>
      </div>
      <div className={styles.Box}>
        <span>{description}</span>
      </div>
      <div className={styles.Box_1}>
        <RoleInteractionIndicator userRole="New users" access={false} />
        <RoleInteractionIndicator userRole="Users" access={interactionLimitBanner.usersHaveAccess} />
        <RoleInteractionIndicator userRole="Contributors" access={interactionLimitBanner.contributorsHaveAccess} />
        <RoleInteractionIndicator userRole="Collaborators" access />
        {interactionLimitBanner.inOrganization && <RoleInteractionIndicator userRole="Organization members" access />}
      </div>
      {interactionLimitBanner.adminText && interactionLimitBanner.adminLink && interactionLimitBanner.disablePath && (
        <div className={styles.Box_2}>
          <Button
            onClick={async () => {
              const formData = new FormData()
              formData.append('interaction_setting', 'NO_LIMIT')
              formData.append('_method', 'put')

              if (interactionLimitBanner.disablePath) {
                await verifiedFetch(interactionLimitBanner.disablePath, {
                  body: formData,
                  method: 'POST',
                  redirect: 'manual',
                })
              }

              window.location.reload()
            }}
          >
            Disable
          </Button>
          <div className={styles.Box_3}>
            &nbsp;or view&nbsp;
            <Link inline href={interactionLimitBanner.adminLink}>
              {interactionLimitBanner.adminText}
            </Link>
          </div>
        </div>
      )}
    </Flash>
  )
}

function RoleInteractionIndicator({userRole, access}: {userRole: string; access: boolean}) {
  const IconComponent = access ? CheckIcon : XIcon
  return (
    <div className={styles.Box_4}>
      <IconComponent className={access ? styles.iconSuccess : styles.iconDanger} />
      {userRole}
    </div>
  )
}
